// ContextPanel — the chat's one right panel (right panel v2, named "Context"): ONE
// pinned header row (back, Context > entity, Open in library, close) over
// either the panel HOME (three groups: material in this chat, sources and where
// they are used, other files) or one FOCUSED entity (a material under its two
// lenses, a source with "Used in", a package, a file).
//
// The composition seam: it reads the shell context, builds the chat's world
// (the pure `worldOf` / `homeOf`), and maps every intent to a frame transition
// or an existing shell command. The focus itself is frame state
// (`frame.panelFocus`), written only by the focus transitions.

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { ClaimIntents } from "../../../components/ClaimRow/ClaimRow.js";
import {
    elementFocusTarget,
    pageFocusTarget,
} from "../../../components/Navigation/focusTarget.js";
import type {
    MaterialRef,
    ResourceRef,
} from "../../../components/Navigation/types.js";
import {
    type HeaderFocus,
    PanelFocusHeader,
} from "../../../components/PanelFocus/PanelFocusHeader.js";
import type { StatementSource } from "../../../components/QualitySummary/QualitySummary.js";
import { ScrollArea } from "../../../components/ScrollArea/ScrollArea.js";
import { GenerationSideView, isGeneration } from "../../../modules/material.js";
import {
    type KontextFold,
    type KontextGroup,
    readKontextFold,
    toggled,
    writeKontextFold,
} from "../../kontextFold.js";
import { pendingGroundingFor } from "../../pendingGrounding.js";
import type { ClaimQuery } from "../../ports/workspace.js";
import { readStatusOf } from "../../sourceReadStatus.js";
import { toComposerResourceRef } from "../constants.js";
import { useAppShell } from "../context.js";
import { conversationResources } from "../conversationResources.js";
import { PanelEntityView } from "../PanelEntityView.js";
import {
    type AddSection,
    GoalGroupBody,
    type GoalIntents,
    goalLensAnswer,
    goalLensRead,
} from "./GoalViews.js";
import {
    type Ask,
    type ChatGoal,
    type GoalObjective,
    goalSourcesOf,
    type Passage,
    requirementIdsOf,
    toneOfMaterial,
} from "./goal.js";
import { homeOf, worldOf } from "./home.js";
import { PanelHome } from "./PanelHome.js";
import { revealChatReplies } from "./revealChatReplies.js";
import { useClaims } from "./useClaims.js";
import { useConversationGoals } from "./useConversationGoals.js";
import {
    qualityVersionOf,
    useMaterialQualities,
} from "./useMaterialQualities.js";

export const ContextPanel = (): ReactNode => {
    const {
        frame,
        dispatch,
        refFor,
        openRef,
        ports,
        library,
        activeContextItems,
        composerMentions,
        activeConversationId,
        pendingGrounding,
        sessionFor,
        materialSide,
        requestUploadForSource,
        introduceIntoActiveContext,
        conversationSummaries,
        sendChat,
    } = useAppShell();
    const { t, i18n } = useLingui();
    const { panelFocus } = frame;
    // Which home groups rest folded: a per-device preference (kontextFold),
    // read once at mount, written on every toggle.
    const [fold, setFold] = useState<KontextFold>(readKontextFold);
    const toggleGroup = (group: KontextGroup) => {
        const next = toggled(fold, group);
        setFold(next);
        writeKontextFold(next);
    };

    // The chat's world: every resource the conversation involves and HOW it
    // came in (D5's `conversationResources`), each material's quality read, and
    // the replies' citations. The quality reads re-run on every library
    // re-read (the post-turn world refresh), so a turn that adds citations
    // shows them live (UX review G2).
    const entries = sessionFor(activeConversationId).conversation;
    const involved = conversationResources({
        attachedIds: [
            ...activeContextItems.map((item) => item.id),
            ...composerMentions.map((item) => item.id),
            ...pendingGroundingFor(pendingGrounding, activeConversationId).map(
                (ref) => ref.id,
            ),
        ],
        entries,
        resolve: refFor,
    });
    // The chat's materials, plus a focused material from elsewhere (an "other
    // file"), so its lenses read the same live quality.
    const versionOf = new Map(
        (library?.materials ?? []).map((m) => [m.id, qualityVersionOf(m)]),
    );
    const qualities = useMaterialQualities(
        ports.evaluateMaterials,
        [
            ...involved.flatMap(({ ref, involvement }) =>
                ref.type === "material" && involvement === "created"
                    ? [ref.id]
                    : [],
            ),
            ...(panelFocus.type === "entity" &&
            panelFocus.ref.type === "material"
                ? [panelFocus.ref.id]
                : []),
        ]
            .filter((id, i, ids) => ids.indexOf(id) === i)
            .map((id) => ({ id, version: versionOf.get(id) })),
        library,
    );
    const qualityOf = (id: string) => qualities.get(id) ?? null;
    const world = worldOf({ involved, entries, qualityOf, resolve: refFor });

    // The chat's goals, measured over its materials: the "Mål" group and every
    // material's third lens project from this one read.
    const {
        goals,
        earlier,
        reload: reloadGoals,
    } = useConversationGoals(
        ports.conversationGoals,
        activeConversationId,
        world.materials.map((m) => m.ref.id),
        library,
        entries.length,
    );
    const titleOf = (id: string): string | undefined => refFor(id)?.title;
    // Prefill the composer; the author reads, edits and sends (never sent here).
    const prefill = (text: string) => {
        sendChat({
            type: "edited_draft",
            conversationId: activeConversationId,
            text,
        });
    };
    const askText = (o: GoalObjective, ask: Ask): string => {
        const statement = o.statement;
        switch (ask) {
            case "add_exercises":
                return t`Add questions to the material that practise this learning objective: "${statement}"`;
            case "explain":
                return t`Explain this learning objective in the material: "${statement}"`;
            case "cover":
                return t`Cover this learning objective in the material, both explained and practised: "${statement}"`;
        }
    };
    const addText = (goal: ChatGoal, section: AddSection): string => {
        const name = goal.purpose ?? goal.title;
        switch (section) {
            case "objective":
                return t`Help me add a learning objective to the goal "${name}". Suggest one and ask me before you add it.`;
            case "term":
                return t`Help me add a term to the goal "${name}". Offer terms I already have first, and ask me before you add one.`;
            case "audience":
                return t`Help me say who the goal "${name}" is for. Ask me before you set it.`;
            case "sub_goal":
                return t`Help me split the goal "${name}" into sub-goals, each with its own learning objectives. Propose them and ask me before you create anything.`;
        }
    };
    const setGoalText = t`Help me set a goal for this chat. Start by asking me what I want it to lead to, and help me put that into words before you ask about anything else.`;
    const openPassage = (p: Passage) => {
        const page = p.page === undefined ? undefined : pageFocusTarget(p.page);
        dispatch({
            type: "show-in-panel",
            ref: { type: "source", id: p.sourceId, title: p.sourceTitle },
            target: { ...page, quote: p.quote },
        });
    };
    // The knowledge bank's claims: every goal's (and sub-goal's), and the
    // focused source's (FR-13.9). Correct / Remove prefill a request to the
    // agent (it revises or retracts; no inline edit).
    const goalIds = (gs: readonly ChatGoal[]): readonly string[] =>
        gs.flatMap((g) => [g.contextId, ...goalIds(g.subGoals)]);
    const focusedSourceId =
        panelFocus.type === "entity" && panelFocus.ref.type === "source"
            ? panelFocus.ref.id
            : undefined;
    const claimQueries: readonly ClaimQuery[] = [
        ...goalIds(goals).map(
            (goalId): ClaimQuery => ({ type: "claims_for_goal", goalId }),
        ),
        ...(focusedSourceId === undefined
            ? []
            : [
                  {
                      type: "claims_of_source",
                      sourceId: focusedSourceId,
                  } satisfies ClaimQuery,
              ]),
    ];
    const claimsOf = useClaims(
        ports.loadClaims,
        claimQueries,
        library,
        entries.length,
    );
    const claimIntents: ClaimIntents = {
        onOpenPassage: (p) =>
            openPassage({
                sourceId: p.sourceId,
                sourceTitle: p.sourceTitle,
                quote: p.exact,
                page: p.page,
            }),
        onCorrect: (c) => {
            const wording = c.wording;
            prefill(
                t`This claim from my uploaded sources needs correcting: "${wording}". Ask me what is wrong, then revise it.`,
            );
        },
        onRemove: (c) => {
            const wording = c.wording;
            prefill(
                t`Remove this claim from my uploaded sources: "${wording}". First tell me what it backs.`,
            );
        },
    };
    const goalIntents: GoalIntents = {
        onOpenPart: (part) =>
            dispatch({
                type: "show-in-panel",
                ref: {
                    type: "material",
                    id: part.materialId,
                    title: titleOf(part.materialId) ?? "",
                },
                target: elementFocusTarget(part.elementId),
            }),
        onOpenPassage: openPassage,
        onOpenRequirement: (r) =>
            dispatch({
                type: "show-in-panel",
                ref: { type: "source", id: r.sourceId, title: r.title },
            }),
        onAskAgent: (o, ask) => prefill(askText(o, ask)),
        materialTitle: titleOf,
        claimsOf: (goalId) => claimsOf({ type: "claims_for_goal", goalId }),
        claim: claimIntents,
    };
    const changeGoal =
        (port: typeof ports.addConversationGoal) => (contextId: string) => {
            port(activeConversationId, contextId).then(reloadGoals);
        };
    const goalLineOf = (materialId: string) => {
        if (goals.length === 0) return undefined;
        const summary = goalLensRead(goals, materialId);
        return {
            answer: goalLensAnswer(true, summary)
                .map((d) => i18n._(d))
                .join(". "),
            tone: toneOfMaterial(summary),
        };
    };
    const goalLens = {
        goals,
        intents: goalIntents,
        onSetGoal: () => prefill(setGoalText),
    };

    const focus = (ref: ResourceRef) => dispatch({ type: "focus-entity", ref });
    const showStatementSource = (
        material: MaterialRef,
        row: StatementSource,
    ) => {
        const target =
            row.page === undefined ? undefined : pageFocusTarget(row.page);
        dispatch({
            type: "show-statement-source",
            source: {
                type: "source",
                id: row.source.id,
                title: refFor(row.source.id)?.title ?? row.source.title,
            },
            origin: {
                type: "statement",
                material,
                statementId: row.statementId,
            },
            ...(target === undefined ? {} : { target }),
        });
    };

    // A material still being made leads with its generation side view.
    const generationOf = (materialId: string): ReactNode => {
        const side = materialSide.model.focus;
        return side._tag === "Focus.Shown" &&
            side.root.id === materialId &&
            isGeneration(side) ? (
            <GenerationSideView focus={side} onMsg={materialSide.send} />
        ) : null;
    };

    // The one step back: to the claim's material when the source was reached
    // from one (v2's named step back), else home.
    const headerFocus: HeaderFocus =
        panelFocus.type === "entity"
            ? {
                  type: "entity",
                  ref: panelFocus.ref,
                  ...(panelFocus.part === undefined
                      ? {}
                      : { part: panelFocus.part }),
                  back:
                      panelFocus.origin === undefined
                          ? { type: "home" }
                          : {
                                type: "material",
                                title: panelFocus.origin.material.title,
                            },
              }
            : { type: "overview" };
    const goHome = () => dispatch({ type: "focus-overview" });
    const goBack = () =>
        panelFocus.type === "entity" && panelFocus.origin !== undefined
            ? dispatch({
                  type: "show-in-panel",
                  ref: panelFocus.origin.material,
                  target: elementFocusTarget(panelFocus.origin.statementId),
              })
            : goHome();

    return (
        <div className="bilda-dock-focus">
            <PanelFocusHeader
                focus={headerFocus}
                onFocusOverview={goHome}
                onBack={goBack}
                // Where it lives (a source's viewer in the Knowledge bank),
                // at the place the panel shows (a cited page).
                onOpenHome={(ref) =>
                    openRef(
                        ref,
                        panelFocus.type === "entity" &&
                            panelFocus.ref.id === ref.id
                            ? panelFocus.target
                            : undefined,
                    )
                }
                onFocusWhole={focus}
            />
            <ScrollArea className="bilda-dock-focus__body">
                {panelFocus.type === "entity" ? (
                    <PanelEntityView
                        entity={panelFocus.ref}
                        target={panelFocus.target}
                        origin={panelFocus.origin}
                        world={world}
                        ports={ports}
                        generation={
                            panelFocus.ref.type === "material"
                                ? generationOf(panelFocus.ref.id)
                                : null
                        }
                        qualityOf={qualityOf}
                        readStatusOf={(id) => readStatusOf(library, id)}
                        onShowInPanel={(ref, target) =>
                            dispatch({
                                type: "show-in-panel",
                                ref,
                                ...(target === undefined ? {} : { target }),
                            })
                        }
                        onShowStatementSource={showStatementSource}
                        onShowReplies={revealChatReplies}
                        onAttachSource={(ref) =>
                            introduceIntoActiveContext(
                                toComposerResourceRef(ref),
                            )
                        }
                        onReuploadSource={(ref, onRestored) =>
                            requestUploadForSource(ref.id, ref.title, {
                                onRestored,
                            })
                        }
                        goal={goalLens}
                        claims={{
                            ofSource: (sourceId) =>
                                claimsOf({
                                    type: "claims_of_source",
                                    sourceId,
                                }),
                            intents: claimIntents,
                        }}
                    />
                ) : (
                    <PanelHome
                        home={homeOf(
                            world,
                            requirementIdsOf(goals),
                            goalSourcesOf(goals),
                        )}
                        goal={
                            <GoalGroupBody
                                goals={goals}
                                hasMaterial={world.materials.length > 0}
                                intents={goalIntents}
                                onSetGoal={() => prefill(setGoalText)}
                                onAskToAdd={(goal, section) =>
                                    prefill(addText(goal, section))
                                }
                                earlier={earlier.map((g) => ({
                                    ...g,
                                    // Name each chat as the rail does.
                                    chats: g.chats.map((c) => ({
                                        ...c,
                                        title:
                                            conversationSummaries.find(
                                                (s) => s.id === c.id,
                                            )?.title ?? c.title,
                                    })),
                                }))}
                                onChooseGoal={changeGoal(
                                    ports.addConversationGoal,
                                )}
                                onRemoveGoal={changeGoal(
                                    ports.removeConversationGoal,
                                )}
                            />
                        }
                        hasGoal={goals.length > 0}
                        goalLineOf={goalLineOf}
                        fold={fold}
                        onToggleGroup={toggleGroup}
                        onOpenMaterial={focus}
                        onOpenSource={focus}
                        onShowReplies={revealChatReplies}
                        onOpenOther={focus}
                    />
                )}
            </ScrollArea>
        </div>
    );
};
